import { useCallback } from "react";
import {
  artifactCsvUrl,
  messageArtifactDrill,
  messageArtifactView,
  messageArtifactZoom,
  useSwapArtifact,
  type ArtifactSelection,
  type ChatMessage,
} from "@/api/queries";
import { AddTo, SaveAsReport } from "@/components/reports/PinMenus";
import { Chip } from "@/components/ui/Chip";
import { EmptyState } from "@/components/ui/EmptyState";
import { Spinner } from "@/components/ui/Spinner";
import { AlertIcon, ChartIcon, DatabaseIcon } from "@/components/icons";
import { ArtifactCard } from "./ArtifactCard";
import { LinkedScope } from "./LinkedScope";

/**
 * The artifact workspace pane (PRD FR-6.7).
 *
 * It mirrors one answer: the scope that answer was given, the relations it
 * read, the caveats it raised, and its artifacts. Scope and provenance sit at
 * the top rather than beside the chart because they qualify the whole answer —
 * "2026, products, top 5" is the difference between a chart being right and
 * being right about something else (PRD FR-4.4, FR-5.3).
 */

const CONTEXT_LABELS: Array<[string, string]> = [
  ["metric_family", "metric"],
  ["date_range", "period"],
  ["entity", "entity"],
  ["grouping", "grouped by"],
  ["filters", "filtered"],
  ["top_n", "top"],
];

function ScopeChips({ context }: { context: Record<string, unknown> }) {
  const entries = CONTEXT_LABELS.map(([key, label]) => [label, context[key]] as const).filter(
    ([, value]) => typeof value === "string" && value.trim() !== "",
  );
  if (!entries.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {entries.map(([label, value]) => (
        <Chip key={label} tone="accent">
          <span className="text-ink-disabled">{label}</span> {String(value)}
        </Chip>
      ))}
    </div>
  );
}

export function ArtifactPanel({
  message,
  threadId,
  busy,
  onAsk,
}: {
  message: ChatMessage | null;
  threadId: string;
  busy: boolean;
  /** Write a follow-up into the composer (LINK-4's "Ask about …"). */
  onAsk?: (text: string) => void;
}) {
  const swap = useSwapArtifact(threadId);
  const messageId = message?.id ?? "";
  const view = useCallback(
    (index: number, kind: string, selection: ArtifactSelection) =>
      messageArtifactView(messageId, index, kind, selection),
    [messageId],
  );
  const zoom = useCallback(
    (index: number, link: string, key: string) =>
      messageArtifactZoom(messageId, index, link, key),
    [messageId],
  );
  const drill = useCallback(
    (index: number, kind: string, selection: ArtifactSelection) =>
      messageArtifactDrill(messageId, index, kind, selection),
    [messageId],
  );

  if (busy && !message) {
    return (
      <div className="text-ink-secondary flex items-center gap-2 px-5 py-6">
        <Spinner className="size-4" />
        <span className="type-support">Artifacts appear as the answer lands.</span>
      </div>
    );
  }

  if (!message) {
    return (
      <div className="px-5 py-8">
        <EmptyState
          icon={<ChartIcon className="size-6" />}
          title="No artifacts yet"
          detail="Charts, tables and summary cards appear here as answers arrive, built from the query results rather than retyped by the model."
        />
      </div>
    );
  }

  const artifacts = message.artifacts ?? [];
  const answered = message.status === "complete" && artifacts.length > 0;

  return (
    <div className="flex flex-col gap-4 px-4 py-4">
      <header className="flex flex-col gap-2">
        <div className="flex flex-wrap items-start gap-2">
          <h2 className="type-h4 text-ink min-w-0 flex-1">
            {message.artifact_title || "Answer"}
          </h2>
          {answered ? <SaveAsReport messageId={message.id} /> : null}
        </div>
        <ScopeChips context={message.context ?? {}} />
        {message.sources.length ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <DatabaseIcon className="text-ink-disabled size-3.5" />
            {message.sources.map((source) => (
              <Chip key={source} tone="neutral">
                {source}
              </Chip>
            ))}
          </div>
        ) : null}
        {message.warnings.map((warning) => (
          <p
            key={warning}
            className="bg-warning-subtle text-warning type-support rounded-element flex items-start gap-2 px-3 py-2"
          >
            <AlertIcon className="mt-0.5 size-3.5 shrink-0" />
            <span>{warning}</span>
          </p>
        ))}
      </header>

      {artifacts.length ? (
        // One answer is one scope (LINK-2): its artifacts answer to one
        // selection. Keyed by the message so a new answer starts unselected.
        <LinkedScope
          key={message.id}
          links={message.links ?? []}
          view={view}
          drill={drill}
          zoom={zoom}
          onAsk={onAsk}
          className="flex flex-col gap-4"
        >
          {artifacts.map((artifact) => (
            <ArtifactCard
              key={`${message.id}-${artifact.index}`}
              artifact={artifact}
              linkIndex={artifact.index}
              csvUrl={artifactCsvUrl(message.id, artifact.index)}
              swapping={swap.isPending}
              onSwap={(type) =>
                swap.mutateAsync({ messageId: message.id, index: artifact.index, type })
              }
              actions={<AddTo messageId={message.id} artifactIndex={artifact.index} />}
            />
          ))}
        </LinkedScope>
      ) : (
        <EmptyState
          icon={<ChartIcon className="size-6" />}
          title="This answer has no artifacts"
          detail={
            message.steps.some((step) => step.tool === "execute_sql")
              ? "The answer came from SQL but the model attached nothing to show. The trace under the answer has the query and its result."
              : "Nothing was queried for this answer, so there is nothing to chart."
          }
        />
      )}
    </div>
  );
}
